import { useState, useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { Upload, FileText, Users, LogOut, User, Cog, Package, Wrench, Building2, Settings, Home as HomeIcon, Palette, Link2, Menu, X, BarChart3, BarChart2, BookOpen, ClipboardList, ScrollText, ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ToastProvider } from './components/ui/ToastProvider'
import { ThemeProvider, useTheme } from './context/ThemeContext'
import { FeatureProvider, useFeatures, FeatureFlags } from './context/FeatureContext'
import { api, getCompanySettings, getCompanyLogoUrl } from './api/client'
import HelpButton from './components/HelpButton'

// Eagerly load auth-related pages (needed immediately)
import Login from './pages/Login'
import SSOCallback from './pages/SSOCallback'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AcceptInvite from './pages/AcceptInvite'

// Lazy load all other pages for better initial bundle size
const Home = lazy(() => import('./pages/Home'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const AnalyzePrint = lazy(() => import('./pages/AnalyzePrint'))
const HistoryPage = lazy(() => import('./pages/History'))
const Admin = lazy(() => import('./pages/Admin'))
const Processes = lazy(() => import('./pages/Processes'))
const Materials = lazy(() => import('./pages/Materials'))
const AssemblyList = lazy(() => import('./pages/AssemblyList'))
const AssemblyUpload = lazy(() => import('./pages/AssemblyUpload'))
const Customers = lazy(() => import('./pages/Customers'))
const CustomerFormulas = lazy(() => import('./pages/CustomerFormulas'))
const CompanySettings = lazy(() => import('./pages/CompanySettings'))
const ThemePicker = lazy(() => import('./pages/ThemePicker'))
const IntegrationSettings = lazy(() => import('./pages/Settings'))
const Training = lazy(() => import('./pages/Training'))
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'))
const AdminGuide = lazy(() => import('./pages/AdminGuide'))
const Analytics = lazy(() => import('./pages/Analytics'))
const RFQList = lazy(() => import('./pages/RFQList'))
const RFQDetail = lazy(() => import('./pages/RFQDetail'))
const AuditLogPage = lazy(() => import('./pages/AuditLog'))
const AdminSettingsPage = lazy(() => import('./pages/AdminSettings'))

// Single source of truth for sidebar destinations. The Trainer group is a
// subset of the same item definitions the Admin group uses, so labels and
// feature gates can't drift between the two duplicated renderings.
interface NavItem {
  to: string
  label: string
  icon: React.ElementType
  feature?: keyof FeatureFlags
}

const NAV_ITEMS: Record<string, NavItem> = {
  customers: { to: '/customers', label: 'Customers', icon: Building2 },
  processes: { to: '/processes', label: 'Processes', icon: Cog },
  materials: { to: '/materials', label: 'Materials', icon: Package },
  company: { to: '/company', label: 'Company', icon: Settings },
  integrations: { to: '/integrations', label: 'Integrations', icon: Link2, feature: 'jobboss_integration' },
  themes: { to: '/themes', label: 'Themes', icon: Palette },
  analytics: { to: '/analytics', label: 'Analytics', icon: BarChart2 },
  audit: { to: '/audit', label: 'Audit Log', icon: ScrollText },
  training: { to: '/training', label: 'Training', icon: BarChart3, feature: 'training_system' },
  guide: { to: '/guide', label: 'Admin Guide', icon: BookOpen },
  portalSettings: { to: '/portal-settings', label: 'Portal Settings', icon: Cog },
  users: { to: '/users', label: 'Users', icon: Users },
}

const TRAINER_GROUP: NavItem[] = [
  NAV_ITEMS.processes, NAV_ITEMS.materials, NAV_ITEMS.training,
]

const ADMIN_GROUP: NavItem[] = [
  NAV_ITEMS.customers, NAV_ITEMS.processes, NAV_ITEMS.materials,
  NAV_ITEMS.company, NAV_ITEMS.integrations, NAV_ITEMS.themes, NAV_ITEMS.analytics,
  NAV_ITEMS.audit, NAV_ITEMS.training, NAV_ITEMS.guide, NAV_ITEMS.portalSettings, NAV_ITEMS.users,
]

function NavLink({ to, children, icon: Icon, onClick, collapsed }: { to: string; children: React.ReactNode; icon: React.ElementType; onClick?: () => void; collapsed?: boolean }) {
  const location = useLocation()
  const isActive = location.pathname === to

  return (
    <Link
      to={to}
      onClick={onClick}
      title={collapsed && typeof children === 'string' ? children : undefined}
      className={`flex items-center gap-2.5 px-4 py-1.5 rounded-md transition-all duration-200 text-sm font-medium ${
        collapsed ? 'lg:justify-center lg:px-2' : ''
      } ${
        isActive
          ? 'nav-forge-active'
          : 'text-[#A8998E] hover:text-white hover:bg-[rgba(234,88,12,0.05)]'
      }`}
    >
      <Icon size={18} strokeWidth={1.5} className={`shrink-0 ${collapsed ? 'lg:h-[22px] lg:w-[22px]' : ''}`} />
      <span className={collapsed ? 'lg:hidden' : ''}>{children}</span>
    </Link>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EA580C]"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EA580C]"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!isAdmin) {
    return <Navigate to="/home" replace />
  }

  return <>{children}</>
}

function TrainerRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, isTrainer, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EA580C]"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!isAdmin && !isTrainer) {
    return <Navigate to="/home" replace />
  }

  return <>{children}</>
}

function NotFound() {
  return (
    <div className="max-w-lg mx-auto text-center py-16">
      <h1 className="text-2xl font-bold mb-2">Page not found</h1>
      <p className="text-theme-text-secondary mb-6">That URL doesn't match anything in Forge-Quote.</p>
      <div className="flex items-center justify-center gap-4 text-sm">
        <Link to="/home" className="text-theme-primary hover:text-theme-primary-hover font-medium">Home</Link>
        <Link to="/upload" className="text-theme-primary hover:text-theme-primary-hover font-medium">Upload Prints</Link>
        <Link to="/parts" className="text-theme-primary hover:text-theme-primary-hover font-medium">Parts</Link>
      </div>
    </div>
  )
}

// Loading fallback for lazy-loaded components
function PageLoader() {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EA580C]" />
      <span className="font-display text-xs text-theme-text-secondary tracking-widest uppercase">Loading</span>
    </div>
  )
}

function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout, isAdmin, isTrainer } = useAuth()
  const { theme } = useTheme()
  const { hasFeature } = useFeatures()
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Desktop-only: collapse the sidebar to a slim icon rail (mobile keeps the
  // full-width drawer). Persisted like the section toggles below.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === '1' } catch { return false }
  })
  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev
      try { localStorage.setItem('sidebar_collapsed', next ? '1' : '0') } catch {}
      return next
    })
  }
  const [adminOpen, setAdminOpen] = useState(() => {
    try { return localStorage.getItem('sidebar_admin_open') === '1' } catch { return false }
  })
  const [trainerOpen, setTrainerOpen] = useState(() => {
    try { return localStorage.getItem('sidebar_trainer_open') === '1' } catch { return false }
  })
  // Keep Blueprint special-case for grid background
  const isBlueprint = theme?.name === 'Blueprint'

  const location = useLocation()
  const adminPaths = ['/customers', '/company', '/integrations', '/themes', '/analytics', '/audit', '/guide', '/users', '/portal-settings']
  const trainerPaths = ['/processes', '/materials', '/training']

  const toggleAdmin = () => {
    setAdminOpen(prev => {
      const next = !prev
      try { localStorage.setItem('sidebar_admin_open', next ? '1' : '0') } catch {}
      return next
    })
  }

  const toggleTrainer = () => {
    setTrainerOpen(prev => {
      const next = !prev
      try { localStorage.setItem('sidebar_trainer_open', next ? '1' : '0') } catch {}
      return next
    })
  }

  // Auto-expand admin section when on an admin page
  useEffect(() => {
    if (isAdmin && adminPaths.some(p => location.pathname.startsWith(p))) {
      setAdminOpen(true)
    }
    if ((isAdmin || isTrainer) && trainerPaths.some(p => location.pathname.startsWith(p))) {
      if (isAdmin) setAdminOpen(true)
      else setTrainerOpen(true)
    }
  }, [location.pathname, isAdmin, isTrainer])

  useEffect(() => {
    let isMounted = true
    getCompanySettings()
      .then((settings) => {
        if (isMounted && settings.logo_url) {
          setLogoUrl(getCompanyLogoUrl(settings.logo_url))
        }
      })
      .catch(() => {
        // Silently fail — show default header without logo
      })
    return () => {
      isMounted = false
    }
  }, [])

  const closeSidebar = () => setSidebarOpen(false)

  return (
    <div className="min-h-screen flex font-body">
      {/* Mobile header bar */}
      <div className="fixed top-0 left-0 right-0 z-30 lg:hidden bg-[#1C1412] border-b border-[#2E2420] flex items-center gap-3 px-4 py-2">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-1.5 text-[#A8998E] hover:text-white transition-colors"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
        <span className="font-display font-bold text-sm text-white tracking-widest uppercase">Forge-Quote</span>
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Company Logo"
            className="max-h-6 object-contain ml-auto opacity-80"
          />
        )}
      </div>

      {/* Backdrop overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`w-64 ${collapsed ? 'lg:w-20' : ''} fixed top-0 left-0 h-screen bg-[#1C1412] border-r border-[#2E2420] flex flex-col overflow-y-auto z-50 transition-all duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>

        {/* Orange gradient stripe at very top */}
        <div className="sidebar-stripe" />

        {/* Logo area */}
        <div className={`border-b border-[#2E2420] ${collapsed ? 'px-5 py-5 lg:px-2 lg:py-4' : 'px-5 py-5'}`}>
          {/* Mobile close button */}
          <div className="flex justify-end lg:hidden mb-3">
            <button
              onClick={closeSidebar}
              className="p-1 text-[#A8998E] hover:text-white transition-colors"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>
          <div className={`flex items-start justify-between gap-2 ${collapsed ? 'lg:justify-center' : ''}`}>
            <div className={collapsed ? 'lg:hidden' : ''}>
              <h1 className="font-display font-bold text-base text-white tracking-widest uppercase leading-tight">Forge-Quote</h1>
              <p className="section-label-forge mt-0.5" style={{ color: '#6B5E54' }}>Fabrication Quoting</p>
            </div>
            <button
              onClick={toggleCollapsed}
              className="hidden lg:flex p-1.5 rounded-md text-[#A8998E] hover:text-white hover:bg-[rgba(234,88,12,0.05)] transition-colors"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={18} />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-0.5 px-2">
          {/* Main section */}
          <div className={`px-3 pb-2 ${collapsed ? 'lg:hidden' : ''}`}>
            <span className="section-label-forge">Main</span>
          </div>
          <NavLink to="/home" icon={HomeIcon} onClick={closeSidebar} collapsed={collapsed}>Home</NavLink>
          <NavLink to="/upload" icon={Upload} onClick={closeSidebar} collapsed={collapsed}>Upload Prints</NavLink>
          {hasFeature('assembly_support') && (
            <NavLink to="/weldments" icon={Wrench} onClick={closeSidebar} collapsed={collapsed}>Weldments</NavLink>
          )}
          <NavLink to="/parts" icon={FileText} onClick={closeSidebar} collapsed={collapsed}>Parts</NavLink>
          <NavLink to="/rfq" icon={ClipboardList} onClick={closeSidebar} collapsed={collapsed}>RFQs</NavLink>

          {/* Trainer section */}
          {isTrainer && !isAdmin && (
            <div className="pt-4">
              <button
                onClick={toggleTrainer}
                className={`flex items-center gap-2 px-3 py-1 w-full transition-colors ${collapsed ? 'lg:hidden' : ''}`}
              >
                <span className="section-label-forge flex-1 text-left">Training</span>
                <ChevronDown size={12} className={`text-[#6B5E54] transition-transform ${trainerOpen ? 'rotate-0' : '-rotate-90'}`} />
              </button>
              {/* Collapsed rail shows group items as icons (no headers to click) */}
              {(trainerOpen || collapsed) && (
                <div className={`space-y-0.5 mt-1 ${collapsed && !trainerOpen ? 'hidden lg:block' : ''}`}>
                  {TRAINER_GROUP.filter(item => !item.feature || hasFeature(item.feature)).map(item => (
                    <NavLink key={item.to} to={item.to} icon={item.icon} onClick={closeSidebar} collapsed={collapsed}>{item.label}</NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Admin section */}
          {isAdmin && (
            <div className="pt-4">
              <button
                onClick={toggleAdmin}
                className={`flex items-center gap-2 px-3 py-1 w-full transition-colors ${collapsed ? 'lg:hidden' : ''}`}
              >
                <span className="section-label-forge flex-1 text-left">Admin</span>
                <ChevronDown size={12} className={`text-[#6B5E54] transition-transform ${adminOpen ? 'rotate-0' : '-rotate-90'}`} />
              </button>
              {(adminOpen || collapsed) && (
                <div className={`space-y-0.5 mt-1 ${collapsed && !adminOpen ? 'hidden lg:block' : ''}`}>
                  {ADMIN_GROUP.filter(item => !item.feature || hasFeature(item.feature)).map(item => (
                    <NavLink key={item.to} to={item.to} icon={item.icon} onClick={closeSidebar} collapsed={collapsed}>{item.label}</NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Company logo */}
        {logoUrl && (
          <div className={`border-t border-[#2E2420] px-5 py-3 flex items-center justify-center ${collapsed ? 'lg:hidden' : ''}`}>
            <img
              src={logoUrl}
              alt="Company Logo"
              className="max-h-10 max-w-[180px] object-contain opacity-60"
            />
          </div>
        )}

        {/* User info and logout */}
        <div className={`border-t border-[#2E2420] py-3 ${collapsed ? 'px-4 lg:px-2' : 'px-4'}`}>
          <div className={`flex items-center gap-2.5 mb-2 ${collapsed ? 'lg:justify-center' : ''}`}>
            <div
              className="w-7 h-7 bg-[#2E2420] rounded-md flex items-center justify-center shrink-0"
              title={collapsed ? `${user?.full_name || user?.username} (${user?.email || ''})` : undefined}
            >
              <User size={14} className="text-[#A8998E]" />
            </div>
            <div className={`flex-1 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
              <p className="text-xs font-medium text-white truncate">
                {user?.full_name || user?.username}
              </p>
              <p className="text-[0.65rem] text-[#6B5E54] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            title={collapsed ? 'Sign Out' : undefined}
            className={`flex items-center gap-2 px-3 py-1.5 w-full text-[#A8998E] hover:text-white text-xs rounded-md transition-colors hover:bg-[rgba(234,88,12,0.05)] ${collapsed ? 'lg:justify-center lg:px-2' : ''}`}
          >
            <LogOut size={14} className="shrink-0" />
            <span className={collapsed ? 'lg:hidden' : ''}>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={`flex-1 ${collapsed ? 'lg:ml-20' : 'lg:ml-64'} p-4 lg:p-8 pt-16 lg:pt-8 bg-theme-background bg-grain overflow-x-hidden max-w-[100vw] transition-[margin] duration-300`}
        style={isBlueprint ? { backgroundImage: 'url(/grid.jpg)', backgroundSize: '300px', backgroundRepeat: 'repeat' } : undefined}
      >
        {children}
      </main>
    </div>
  )
}

function OnboardingRedirect({ children }: { children: React.ReactNode }) {
  const { isAdmin, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    if (!isAuthenticated || !isAdmin || checked) return
    if (sessionStorage.getItem('onboarding_dismissed')) {
      setChecked(true)
      return
    }
    if (location.pathname === '/setup') {
      setChecked(true)
      return
    }

    api.get('/onboarding/status')
      .then((res) => {
        if (!res.data.completed) {
          navigate('/setup', { replace: true })
        }
      })
      .catch(() => {})
      .finally(() => setChecked(true))
  }, [isAuthenticated, isAdmin])

  return <>{children}</>
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EA580C]"></div>
      </div>
    )
  }

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? "/home" : "/login"} replace />}
      />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/home" replace /> : <Login />}
      />
      <Route path="/sso/callback" element={<SSOCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <OnboardingRedirect>
              <Layout>
                <Suspense fallback={<PageLoader />}>
                  <Home />
                </Suspense>
              </Layout>
            </OnboardingRedirect>
          </ProtectedRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analyze/:jobId"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AnalyzePrint />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/processes"
        element={
          <TrainerRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Processes />
              </Suspense>
            </Layout>
          </TrainerRoute>
        }
      />
      <Route
        path="/materials"
        element={
          <TrainerRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Materials />
              </Suspense>
            </Layout>
          </TrainerRoute>
        }
      />
      <Route
        path="/parts"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <HistoryPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/company"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <CompanySettings />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/themes"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <ThemePicker />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <IntegrationSettings />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Customers />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/customers/:customerId/formulas"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <CustomerFormulas />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/guide"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AdminGuide />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Analytics />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AuditLogPage />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/portal-settings"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AdminSettingsPage />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/setup"
        element={
          <AdminRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <OnboardingWizard />
              </Suspense>
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/training"
        element={
          <TrainerRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Training />
              </Suspense>
            </Layout>
          </TrainerRoute>
        }
      />
      <Route
        path="/rfq"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <RFQList />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rfq/:rfqId"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <RFQDetail />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/weldments"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AssemblyList />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/weldments/new"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AssemblyUpload />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Legacy URL redirects — routes were renamed to match their nav labels */}
      <Route path="/dashboard" element={<Navigate to="/upload" replace />} />
      <Route path="/history" element={<Navigate to="/parts" replace />} />
      <Route path="/assemblies" element={<Navigate to="/weldments" replace />} />
      <Route path="/assemblies/new" element={<Navigate to="/weldments/new" replace />} />
      <Route path="/admin-settings" element={<Navigate to="/portal-settings" replace />} />
      <Route path="/admin" element={<Navigate to="/users" replace />} />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Layout>
              <NotFound />
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

function AuthedHelpButton() {
  const { isAuthenticated } = useAuth()
  if (!isAuthenticated) return null
  return <HelpButton />
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <FeatureProvider>
          <AuthProvider>
            <ToastProvider>
              <AppRoutes />
              <AuthedHelpButton />
            </ToastProvider>
          </AuthProvider>
        </FeatureProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App
